import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, FileText, Shield, LogOut, Phone, ChevronDown, LifeBuoy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../utils/initials';

export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) {
    return (
      <Link
        to="/login"
        className="px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2"
        style={{ backgroundColor: '#0a0a0a', color: '#ffffff' }}
        data-testid="topbar-login-link"
      >
        <User className="h-4 w-4" />
        Login
      </Link>
    );
  }

  const initials = getInitials(user.name || user.email);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center rounded-full hover:bg-zinc-100"
        style={{ width: 44, height: 44 }}
        data-testid="topbar-user-button"
      >
        {(user.profile_photo || user.picture) ? (
          <img
            src={user.profile_photo || user.picture}
            alt=""
            className="h-11 w-11 rounded-full object-cover ring-2 ring-[#C9A962] ring-offset-2 ring-offset-white shadow-lg shadow-[#C9A962]/30"
            data-testid="user-avatar-photo"
          />
        ) : (
          <span
            className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold ring-2 ring-[#C9A962] ring-offset-2 ring-offset-white shadow-lg shadow-[#C9A962]/30"
            style={{ backgroundColor: '#C9A962', color: '#0a0a0a' }}
            data-testid="user-avatar-initials"
          >
            {initials}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-zinc-600 hidden sm:block" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-lg shadow-2xl bg-white overflow-hidden"
          style={{ border: '1px solid #e4e4e7' }}
          data-testid="user-menu-popover"
        >
          <div className="px-4 py-3 bg-zinc-50" style={{ borderBottom: '1px solid #e4e4e7' }}>
            <p className="text-sm font-bold text-zinc-900 truncate" data-testid="user-menu-name">{user.name || 'Profile'}</p>
            <p className="text-xs text-zinc-500 truncate" data-testid="user-menu-email">{user.email}</p>
            <p className="text-[10px] uppercase tracking-widest mt-1" style={{ color: '#C9A962' }}>{(user.role || 'user').replace('_', ' ')}</p>
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/profile'); }}
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-zinc-50 flex items-center gap-3 text-zinc-800"
            data-testid="user-menu-profile"
          >
            <User className="h-4 w-4" /> Profile
          </button>
          {/* Contact Us — visible only on customer-side accounts (Property
              Managers and Tenants/Residents). NECS employees (admin,
              manager, supervisor, crew leader, site agent, driver, staff)
              don't see it because they ARE the support team. */}
          {['property_manager', 'tenant', 'tenant_resident'].includes(user.role) && (
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="w-full px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-3 text-zinc-800"
              data-testid="user-menu-contact"
            >
              <Phone className="h-4 w-4" /> Contact Us
            </Link>
          )}

          {/* Internal Help Desk — only NECS employees see this. Routes
              to the internal support chat / IT ticket form, mirroring the
              customer-facing "Contact Us" but for staff workflows. */}
          {['admin', 'company_manager', 'manager', 'supervisor', 'crew_leader',
            'agent', 'site_agent', 'staff', 'driver'].includes(user.role) && (
            <Link
              to="/help-desk"
              onClick={() => setOpen(false)}
              className="w-full px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-3 text-zinc-800"
              data-testid="user-menu-help-desk"
            >
              <LifeBuoy className="h-4 w-4" /> Internal Help Desk
            </Link>
          )}
          <Link
            to="/privacy"
            onClick={() => setOpen(false)}
            className="w-full px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-3 text-zinc-800"
            data-testid="user-menu-privacy"
          >
            <Shield className="h-4 w-4" /> Privacy Policy
          </Link>
          <Link
            to="/terms"
            onClick={() => setOpen(false)}
            className="w-full px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-3 text-zinc-800"
            data-testid="user-menu-terms"
          >
            <FileText className="h-4 w-4" /> Terms of Service
          </Link>

          <button
            type="button"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 font-semibold"
            style={{ borderTop: '1px solid #e4e4e7', color: '#dc2626' }}
            data-testid="user-menu-signout"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
